import { useState } from 'react';
import { MemberEndorsementClient, MemberEndorsementDTO, ApiResponse_string, UpdateMemberEndorsementStatusDTO } from '../services/apiClient';
import { API_BASE_URL } from '../config/config';
import axiosInstance from '../services/axiosInstance';
import { handleHookError } from '../utils/hookUtils/handleHookError';

export const useMemberEndorsementApi = () => {
    const apiClient = new MemberEndorsementClient(API_BASE_URL, axiosInstance);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const create = async (data: MemberEndorsementDTO) => {
        setIsLoading(true);
        try {
            await apiClient.createMemberEndorsement(data);
        } catch (err: any) {
            setError(err.message || 'Error creating MemberEndorsement');
        } finally {
            setIsLoading(false);
        }
    };

    const edit = async (id: string, data: MemberEndorsementDTO) => {
        setIsLoading(true);
        try {
            await apiClient.updateMemberEndorsement(id, data);
        } catch (err: any) {
            setError(err.message || 'Error updating MemberEndorsement');
        } finally {
            setIsLoading(false);
        }
    };

    const getById = async (id: string) => {
        setIsLoading(true);
        try {
            return await apiClient.getMemberEndorsementById(id);
        } catch (err: any) {
            setError(err.message || 'Error fetching MemberEndorsement');
        } finally {
            setIsLoading(false);
        }
    };

    const search = async (filters: any) => {
        setIsLoading(true);
        try {
            return await apiClient.searchMemberEndorsements(filters);
        } catch (err: any) {
            setError(err.message || 'Error searching MemberEndorsements');
        } finally {
            setIsLoading(false);
        }
    };

    const remove = async (id: string) => {
        setIsLoading(true);
        try {
            await apiClient.deleteMemberEndorsement(id);
        } catch (err: any) {
            setError(err.message || 'Error deleting MemberEndorsement');
        } finally {
            setIsLoading(false);
        }
    };

    const extract = async (
        filters: any
    ): Promise<ApiResponse_string> => {
        setIsLoading(true);
        try {
            return await apiClient.exportMemberEndorsements(filters);
        } catch (err: unknown) {
            handleHookError(err, 'Error exporting MemberEndorsements', setError);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const restoreFromDeleted = async (id: string) => {
        setIsLoading(true);
        try {
            await apiClient.restoreFromDeletedEndorsement(id);
        } catch (err: any) {
            setError(err.message || 'Error restoring Member from deleted');
        } finally {
            setIsLoading(false);
        }
    };

    const getCertificate = async (id: string): Promise<string> => {
        setIsLoading(true);
        try {
            const response = await apiClient.getMemberEndorsementCertificate(id);
            if (response.success && response.data) {
                return response.data;
            } else {
                throw new Error(response.message || 'Failed to generate certificate');
            }
        } catch (err: unknown) {
            handleHookError(err, 'Error generating certificate', setError);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const getDeclaration = async (id: string): Promise<string> => {
        setIsLoading(true);
        try {
            const response = await apiClient.getMemberEndorsementDeclaration(id);
            if (response.success && response.data) {
                return response.data;
            } else {
                throw new Error(response.message || 'Failed to generate declaration');
            }
        } catch (err: unknown) {
            handleHookError(err, 'Error generating declaration', setError);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const getHtmlCertificate = async (id: string): Promise<string> => {
        setIsLoading(true);
        try {
            const response = await apiClient.getMemberEndorsementHtmlCertificate(id);
            if (response.success && response.data) {
                return response.data;
            } else {
                throw new Error(response.message || 'Failed to generate HTML certificate');
            }
        } catch (err: unknown) {
            handleHookError(err, 'Error generating HTML certificate', setError);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const getDistinctYears = async (): Promise<number[]> => {
        setIsLoading(true);
        try {
            const response = await apiClient.getMemberEndorsementYears();
            return (response.data as unknown as number[]) ?? [];
        } catch (err: any) {
            setError(err.message || 'Error fetching endorsement years');
            return [];
        } finally {
            setIsLoading(false);
        }
    };

    const getFeedback = async (id: string): Promise<string | undefined> => {
        setIsLoading(true);
        try {
            const response = await apiClient.getMemberEndorsementFeedback(id);
            return response.data ?? undefined;
        } catch (err: any) {
            setError(err.message || 'Error fetching MemberEndorsement feedback');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const updateStatus = async (data: UpdateMemberEndorsementStatusDTO) => {
        setIsLoading(true);
        try {
            return await apiClient.updateMemberEndorsementStatus(data);
        } catch (err: any) {
            setError(err.message || 'Error updating endorsement statuses');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    return {
        create,
        edit,
        getById,
        search,
        extract,
        remove,
        getCertificate,
        getDeclaration,
        getHtmlCertificate,
        restoreFromDeleted,
        updateStatus,
        getDistinctYears,
        getFeedback,
        isLoading,
        error
    };
};